import 'swiper/css/bundle';

import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { PaginationOptions } from 'swiper/types/modules/pagination';
import { SwiperOptions } from 'swiper/types/swiper-options';
let figureOffsetHeight: number;
const calculateArrowOffset = (swiperInstance: Swiper) => {
  const paginationBtn =
    swiperInstance.el?.querySelector<HTMLElement>('.slider-button');
  if (!paginationBtn) return;
  const arrowOffsetRaw = figureOffsetHeight / 2;
  const paginationBtnHalfSize = paginationBtn.offsetHeight / 2;
  const arrowOffset = `${arrowOffsetRaw - paginationBtnHalfSize}px`;
  swiperInstance.el.style.setProperty('--arrow-top', arrowOffset);
};
const checkAndCalculateArrowOffset = (swiperInstance: Swiper) => {
  const newOffset =
    swiperInstance.el.querySelector('figure')?.offsetHeight ||
    swiperInstance.height;
  if (typeof newOffset === 'undefined') return;
  if (newOffset != figureOffsetHeight) {
    figureOffsetHeight = newOffset;
    calculateArrowOffset(swiperInstance);
  }
};
const autoplaySettings = (selector: string): SwiperOptions['autoplay'] => {
  const slider = document.querySelector<HTMLElement>(selector);
  const autoplay = slider?.dataset.autoplay;
  const delay = tryParseInt(slider?.getAttribute('data-speed'), 0);
  return autoplay === 'true'
    ? {
        delay,
        pauseOnMouseEnter: true,
        disableOnInteraction: false
      }
    : false;
};
const initSlider = (slider: HTMLElement, options: SwiperOptions = {}) => {
  // Remove loading class from container
  slider.classList.remove('is-loading');
  const slidesTablet =
    slider.dataset.slidesPerView == 'auto'
      ? 'auto'
      : tryParseFloat(slider.dataset.slidesTablet, 4);
  const slidesDesktop =
    slider.dataset.slidesPerView == 'auto'
      ? 'auto'
      : tryParseFloat(slider.dataset.slidesDesktop, 4);
  const spaceBetween = tryParseInt(slider.dataset.spaceBetween, 16);
  const slidesPerView =
    slider.dataset.slidesPerView == 'auto'
      ? 'auto'
      : tryParseFloat(slider.dataset.slidesPerView, 2.2);
  const slidesLoop = slider.dataset.slidesLoop === 'true';
  const defaultOptions: SwiperOptions = {
    modules: [Navigation, Pagination],
    spaceBetween: spaceBetween,
    slidesPerView: slidesPerView,
    loop: slidesLoop,
    navigation: {
      nextEl: '.slider-next',
      prevEl: '.slider-prev'
    },
    pagination: {
      el: '.slider-pagination',
      type: 'bullets'
    }
  };
  const sliderPagination: { pagination: PaginationOptions | boolean } =
    slider.dataset.slidesPagination === 'false'
      ? { pagination: false }
      : {
          pagination: {
            el: '.slider-pagination',
            dynamicBullets: true
          }
        };
  const dataOptions: SwiperOptions = {
    ...sliderPagination,
    breakpoints: {
      768: {
        slidesPerView: slidesTablet,
        spaceBetween: spaceBetween
      },
      1024: {
        slidesPerView: slidesDesktop,
        spaceBetween: spaceBetween
      }
    },
    on: {
      init(swiperInstance) {
        // TODO: Don't use element selector, this code breaks if product image is not wrapped in a <figure> (!!!)
        const firstImageContainer =
          swiperInstance.el.querySelector('figure') || swiperInstance.el;
        if (firstImageContainer) {
          figureOffsetHeight = firstImageContainer.offsetHeight;
        }
        calculateArrowOffset(swiperInstance);
      },
      resize(swiperInstance) {
        checkAndCalculateArrowOffset(swiperInstance);
      },
      destroy(swiperInstance) {
        window.removeEventListener('resize', () =>
          checkAndCalculateArrowOffset(swiperInstance)
        );
      }
    }
  };
  // Merge options if they are objects
  const isOptionsEmpty = !options || Object.keys(options).length === 0;
  const extendedDataOptions = isOptionsEmpty
    ? dataOptions
    : Object.fromEntries(
        (Object.keys(dataOptions) as (keyof SwiperOptions)[]).map((key) => {
          const dataValue = dataOptions[key];
          const optionValue = options[key];
          if (
            typeof dataValue === 'object' &&
            dataValue !== null &&
            typeof optionValue === 'object' &&
            optionValue !== null
          ) {
            return [
              key,
              {
                ...dataValue,
                ...optionValue
              }
            ];
          }
          return [key, optionValue !== undefined ? optionValue : dataValue];
        })
      );
  const swiperOptions = { ...defaultOptions, ...extendedDataOptions };
  new Swiper(slider, swiperOptions);
};
export const loadSlider = (
  wrapperElement: HTMLElement,
  options: SwiperOptions
) => {
  const slides = new Array<HTMLElement>(
    ...wrapperElement.querySelectorAll<HTMLElement>('.swiper-slide')
  );

  if (slides.length) {
    initSlider(wrapperElement, options);
  } else {
    // Break interval if it reaches +5 sec (20 * 250ms)
    let tries = 0;
    const maxTries = 20;
    const intervalDelay = 250;
    const intervalId = setInterval(() => {
      tries++;
      const currentSlides = new Array<HTMLElement>(
        ...wrapperElement.querySelectorAll<HTMLElement>('.swiper-slide')
      );
      if (currentSlides.length) {
        clearInterval(intervalId);
        initSlider(wrapperElement, options);
      } else if (tries === maxTries) {
        clearInterval(intervalId);
      }
    }, intervalDelay);
  }
};
export const loadSlideshow = (selector: string) => {
  const slider = document.querySelector<HTMLElement>(selector);
  if (!slider) return;

  const autoplay = autoplaySettings(selector);
  const sliderPagination: { pagination: PaginationOptions | boolean } =
    slider?.dataset.slidesPagination === 'false'
      ? { pagination: false }
      : {
          pagination: {
            el: selector + ' .slider-pagination',
            type: 'bullets'
          }
        };
  const slideshowOptions: SwiperOptions = {
    ...sliderPagination,
    modules: [Autoplay, Pagination, Navigation],
    slidesPerView: 1,
    autoplay,
    on: {},
    breakpoints: {},
    speed: 500
  };
  loadSlider(slider, slideshowOptions);
};
const tryParseInt = (value: string | null | undefined, fallBack: number) => {
  if (value === null || typeof value === 'undefined') {
    return fallBack;
  }
  const integer = Number.parseInt(value);
  return !Number.isNaN(integer) ? integer : fallBack;
};

const tryParseFloat = (value: string | null | undefined, fallBack: number) => {
  if (value === null || typeof value === 'undefined') {
    return fallBack;
  }
  const integer = Number.parseFloat(value);
  return !Number.isNaN(integer) ? integer : fallBack;
};
